import React from 'react';
import { cn } from '@utils/cn';
import { Check } from 'lucide-react';

interface Step {
  id: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
}

interface ProgressStepsProps {
  steps: Step[];
  currentStep: string;
  completedSteps: string[];
  className?: string;
}

export const ProgressSteps: React.FC<ProgressStepsProps> = ({
  steps,
  currentStep,
  completedSteps,
  className,
}) => {
  return (
    <div className={cn("", className)}>
      <nav aria-label="Progress">
        <ol className="flex items-center space-x-5">
          {steps.map((step, index) => {
            const isCompleted = completedSteps.includes(step.id);
            const isCurrent = step.id === currentStep;
            const isLast = index === steps.length - 1;

            return (
              <li key={step.id} className={cn("flex items-center", !isLast && "flex-1")}>
                <div className="flex items-center">
                  {/* Step indicator */}
                  <div
                    className={cn(
                      "flex h-10 w-10 items-center justify-center rounded-full border-2",
                      {
                        "border-ncaa-primary bg-ncaa-primary text-white": isCompleted,
                        "border-ncaa-primary bg-white text-ncaa-primary": isCurrent,
                        "border-gray-300 bg-white text-gray-500": !isCompleted && !isCurrent,
                      }
                    )}
                  >
                    {isCompleted ? (
                      <Check className="h-5 w-5" />
                    ) : step.icon ? (
                      step.icon
                    ) : (
                      <span className="text-sm font-semibold">{index + 1}</span>
                    )}
                  </div>

                  {/* Step info */}
                  <div className="ml-4 min-w-0 hidden sm:block">
                    <p
                      className={cn(
                        "text-sm font-medium",
                        {
                          "text-ncaa-primary": isCurrent || isCompleted,
                          "text-gray-500": !isCurrent && !isCompleted,
                        }
                      )}
                    >
                      {step.title}
                    </p>
                    {step.description && (
                      <p className="text-sm text-gray-500">{step.description}</p>
                    )}
                  </div>
                </div>

                {/* Connector line */}
                {!isLast && (
                  <div
                    className={cn(
                      "ml-4 flex-1 border-t-2",
                      {
                        "border-ncaa-primary": isCompleted,
                        "border-gray-300": !isCompleted,
                      }
                    )}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
};